<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { fetchDungeons, type DungeonCatalogEntry, type DungeonRunState } from '../game/dungeons-api';
import { heistArt } from '../game/art';
import { confirmAction } from '../game/confirm';
import FightScreen from './FightScreen.vue';
import { startHeist, type FightView } from '../game/mobfight-api';
import cashIcon from '../assets/omerta/icons/cash.webp';
import goldBarsIcon from '../assets/omerta/icons/gold-bars.webp';
import nerveIcon from '../assets/omerta/icons/nerve.webp';

// Bubble nerve/cash/xp changes up so HeroScreen refreshes the header.
const props = defineProps<{ heroClass?: string }>();
const emit = defineEmits<{ (e: 'updated'): void }>();

const loading = ref(true);
const error = ref('');
const dungeons = ref<DungeonCatalogEntry[]>([]);
const run = ref<DungeonRunState | null>(null);

// The dungeon currently in the interactive tactical arena (null = catalog view).
const heistTarget = ref<string | null>(null);
const heistInitial = ref<FightView | null>(null); // the run we started, handed to FightScreen

const activeDungeon = computed(() =>
  run.value ? dungeons.value.find((d) => d.id === run.value!.dungeonId) ?? null : null,
);

const hpPct = computed(() =>
  run.value && run.value.maxHp > 0
    ? Math.max(0, Math.min(100, Math.round((run.value.hp / run.value.maxHp) * 100)))
    : 0,
);
const hpTone = computed(() => (hpPct.value > 50 ? '#6f9e54' : hpPct.value > 25 ? '#d9a441' : '#c0504d'));

async function load() {
  try {
    const res = await fetchDungeons();
    dungeons.value = res.dungeons;
    run.value = res.activeRun;
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
}

onMounted(load);

/** Open the tactical arena on a dungeon — starts a fresh run, or resumes an
 *  active one (the backend's /mobfight/heist handles both). Fresh runs charge
 *  nerve at the door, so they go through the confirmation card first. */
async function begin(id: string) {
  error.value = '';
  const resuming = run.value?.status === 'ACTIVE' && run.value.dungeonId === id;
  if (!resuming) {
    const d = dungeons.value.find((x) => x.id === id);
    const ok = await confirmAction({
      kicker: 'Case the joint',
      title: d ? `Run ${d.name}?` : 'Run this job?',
      icon: heistArt(id) || undefined,
      rows: d ? [
        { label: 'Rooms', value: String(d.totalStages), tone: 'dim' },
        { label: 'Door charge', value: String(d.nerveCost), icon: nerveIcon },
        { label: 'Full take', value: d.rewardCash.toLocaleString(), icon: cashIcon, tone: 'success' },
      ] : [],
      note: 'Damage carries room to room — bail early and you keep what you banked.',
      confirmLabel: 'Case the joint',
    });
    if (!ok) return;
  }
  // Start the run HERE, not inside the fight screen: opening it is what checks the
  // door charge (nerve), the crew's HP and whether a run is already ACTIVE. Doing
  // it here keeps "Not enough nerve" on this panel instead of throwing the player
  // onto a fight screen with nothing but an error on it.
  try {
    heistInitial.value = await startHeist(id);
    heistTarget.value = id;
  } catch (e) {
    error.value = (e as Error).message;
  }
}

/** The tactical arena closed — refresh the catalog/run + let the header refresh. */
function onHeistDone() {
  heistTarget.value = null;
  void load();
  emit('updated');
}

/** 72h post-clear cooldown (server-enforced; QA: repeat-heist money glitch). */
function coolingDown(d: DungeonCatalogEntry): boolean {
  return d.cooldownUntil != null && d.cooldownUntil > Date.now();
}
function cooldownLabel(d: DungeonCatalogEntry): string {
  const ms = Math.max(0, (d.cooldownUntil ?? 0) - Date.now());
  const h = Math.ceil(ms / 3_600_000);
  return h >= 48 ? `${Math.ceil(h / 24)}d` : `${h}h`;
}

function ctaLabel(d: DungeonCatalogEntry): string {
  if (d.locked) return `Off limits · Lv ${d.unlockLevel}`;
  if (run.value && run.value.status === 'ACTIVE') {
    return run.value.dungeonId === d.id ? 'Back to the job' : 'Finish your job first';
  }
  if (coolingDown(d)) return `Heat's still on · ${cooldownLabel(d)}`;
  return 'Case the joint';
}
const ctaDisabled = (d: DungeonCatalogEntry) =>
  d.locked || coolingDown(d) ||
  (!!run.value && run.value.status === 'ACTIVE' && run.value.dungeonId !== d.id);
</script>

<template>
  <section>
    <!-- banner -->
    <div class="corner-frame relative rounded-lg overflow-hidden mb-5 h-36">
      <img :src="heistArt('speakeasy_raid')" alt="" class="w-full h-full object-cover" />
      <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0.25),rgba(10,7,20,0.94))"></div>
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <div class="absolute bottom-3 left-4">
        <h1 class="font-heading text-2xl text-gold">Heists</h1>
        <span class="font-label uppercase tracking-wider text-sm text-text-dim block">Multi-room jobs — damage carries, nerve pays the door</span>
      </div>
    </div>
    <p class="font-body italic text-text-dim text-sm mb-6">
      Run the job room by room. Bank a cut for every room you clear, bail with what you've grabbed —
      or push to the last door and take the whole score.
    </p>

    <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>
    <p v-if="loading" class="text-text-dim">Casing the joints…</p>

    <!-- ACTIVE RUN — the job in progress -->
    <div
      v-if="!loading && run && run.status === 'ACTIVE' && activeDungeon"
      class="hz-active corner-frame relative mb-8 overflow-hidden"
    >
      <img v-if="heistArt(activeDungeon.id)" :src="heistArt(activeDungeon.id)" alt=""
           class="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div class="absolute inset-0" style="background:linear-gradient(90deg,rgba(10,7,16,0.92),rgba(10,7,16,0.65))"></div>
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>

      <div class="relative flex flex-wrap items-center gap-x-8 gap-y-4">
        <div class="min-w-0">
          <div class="font-label uppercase tracking-[0.22em] text-[10px] text-gold-bright mb-1">Job in progress</div>
          <h2 class="font-heading text-2xl text-gold leading-none">{{ activeDungeon.name }}</h2>
        </div>

        <!-- room pips -->
        <div class="flex items-center gap-2">
          <span class="font-label uppercase tracking-wider text-[10px] text-text-dim">Rooms</span>
          <span class="flex items-center gap-[6px]">
            <i v-for="n in run.totalStages" :key="n" class="hz-pip"
               :class="{ 'hz-pip-clear': n < run.stage, 'hz-pip-now': n === run.stage }"></i>
          </span>
          <span class="font-heading text-sm text-text tabular-nums">{{ run.stage }}/{{ run.totalStages }}</span>
        </div>

        <!-- crew hp -->
        <div class="flex items-center gap-2 min-w-[180px] flex-1 max-w-[300px]">
          <span class="font-label uppercase tracking-wider text-[10px] text-text-dim shrink-0">Crew</span>
          <div class="hz-hp flex-1"><div class="hz-hp-fill" :style="{ width: hpPct + '%', background: hpTone }"></div></div>
          <span class="font-heading text-sm tabular-nums shrink-0" :style="{ color: hpTone }">{{ run.hp }}/{{ run.maxHp }}</span>
        </div>

        <button
          @click="begin(run.dungeonId)"
          class="btn-deco btn-deco--gold ml-auto"
        >Back to the job</button>
      </div>
    </div>

    <!-- CATALOG — heist posters -->
    <div v-if="!loading" class="grid grid-cols-1 md:grid-cols-2 gap-5">
      <article
        v-for="d in dungeons" :key="d.id"
        class="hz-card corner-frame relative flex flex-col overflow-hidden"
        :class="{ 'hz-locked': d.locked || coolingDown(d), 'hz-live': run && run.status === 'ACTIVE' && run.dungeonId === d.id }"
      >
        <template v-if="!d.locked">
          <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
          <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
        </template>

        <!-- cover -->
        <div class="relative h-36 w-full overflow-hidden shrink-0">
          <img v-if="heistArt(d.id)" :src="heistArt(d.id)" :alt="d.name"
               class="hz-cover w-full h-full object-cover" />
          <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0.1),rgba(10,7,20,0.88))"></div>
          <span class="absolute top-2.5 left-3 hz-chip-dark">{{ d.totalStages }} rooms</span>
          <span v-if="d.locked" class="hz-stamp">Off limits &middot; Lv {{ d.unlockLevel }}</span>
          <div class="absolute bottom-2.5 left-3 right-3 flex items-end justify-between gap-2">
            <h2 class="font-heading text-xl leading-tight" :class="d.locked ? 'text-text-dim' : 'text-gold'">{{ d.name }}</h2>
            <!-- room pips preview -->
            <span class="flex items-center gap-[5px] pb-1 shrink-0">
              <i v-for="n in d.totalStages" :key="n" class="hz-pip hz-pip-sm"></i>
            </span>
          </div>
        </div>

        <div class="p-4 flex flex-col flex-1">
          <p class="font-body italic text-sm text-text-dim mb-3 flex-1 leading-snug">{{ d.blurb }}</p>

          <!-- the terms -->
          <div class="flex flex-wrap items-center gap-2 mb-4">
            <span class="hz-chip" title="Nerve to open the door"><img :src="nerveIcon" alt="nerve" class="w-3.5 h-3.5 object-contain shrink-0" />{{ d.nerveCost }}</span>
            <span class="hz-chip" title="Full-clear payout"><img :src="cashIcon" alt="cash" class="w-3.5 h-3.5 object-contain shrink-0" />{{ d.rewardCash.toLocaleString() }}</span>
            <span class="hz-chip hz-chip-xp" title="Full-clear experience">&#9670; {{ d.rewardXp.toLocaleString() }} XP</span>
            <span class="hz-chip hz-chip-first" title="One-time first-clear bonus"><img :src="goldBarsIcon" alt="gold bars" class="w-3.5 h-3.5 object-contain shrink-0" />+{{ d.firstClearPremium }} first clear</span>
          </div>

          <button
            :disabled="ctaDisabled(d)"
            @click="begin(d.id)"
            class="btn-deco btn-deco--gold btn-deco--sm whitespace-normal text-center leading-snug"
          >{{ ctaLabel(d) }}</button>
        </div>
      </article>
    </div>

    <!-- INTERACTIVE TACTICAL ARENA — room-by-room, server-authoritative -->
    <FightScreen
      v-if="heistTarget"
      :heist-id="heistTarget"
      :hero-class="props.heroClass"
      :initial="heistInitial"
      @done="onHeistDone"
    />
  </section>
</template>

<style scoped>
.hz-active {
  padding: 1.15rem 1.4rem;
  border: 1px solid color-mix(in srgb, var(--color-gold) 55%, transparent);
  border-radius: 12px;
  background: var(--color-surface);
  animation: hz-live-glow 2.8s ease-in-out infinite;
}
@keyframes hz-live-glow {
  0%, 100% { box-shadow: 0 0 12px rgba(200,162,76,0.12); }
  50% { box-shadow: 0 0 26px rgba(227,200,120,0.28); }
}

.hz-pip {
  width: 10px; height: 10px; transform: rotate(45deg); border-radius: 1.5px;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(200,162,76,0.35);
}
.hz-pip-sm { width: 7px; height: 7px; }
.hz-pip-clear {
  background: linear-gradient(135deg, var(--color-gold-bright), var(--color-gold));
  box-shadow: 0 0 6px rgba(227,200,120,0.5);
}
.hz-pip-now {
  background: rgba(200,162,76,0.25);
  border-color: var(--color-gold-bright);
  animation: hz-now-pulse 1.6s ease-in-out infinite;
}
@keyframes hz-now-pulse {
  0%, 100% { box-shadow: 0 0 3px rgba(227,200,120,0.4); }
  50% { box-shadow: 0 0 10px rgba(227,200,120,0.9); }
}

.hz-hp {
  height: 8px; border-radius: 4px; overflow: hidden;
  background: var(--color-bg); border: 1px solid var(--color-border);
}
.hz-hp-fill { height: 100%; transition: width 500ms cubic-bezier(0.3, 0.8, 0.3, 1); }

.hz-card {
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.14)), var(--color-surface);
  transition: transform 170ms ease, box-shadow 170ms ease, border-color 170ms ease;
}
.hz-card:not(.hz-locked):hover {
  transform: translateY(-3px);
  border-color: color-mix(in srgb, var(--color-gold) 45%, transparent);
  box-shadow: 0 12px 28px rgba(0,0,0,0.5);
}
.hz-card:not(.hz-locked):hover .hz-cover { transform: scale(1.04); }
.hz-cover { transition: transform 400ms ease; }
.hz-live { border-color: color-mix(in srgb, var(--color-gold) 60%, transparent); }

.hz-locked .hz-cover { filter: grayscale(0.85) brightness(0.7); }
.hz-locked { opacity: 0.85; }

.hz-stamp {
  position: absolute; top: 34%; left: 50%;
  transform: translate(-50%, -50%) rotate(-11deg);
  font-family: var(--font-label, sans-serif); font-size: 12px; letter-spacing: 0.22em; text-transform: uppercase;
  color: rgba(190, 60, 50, 0.92);
  border: 2px solid rgba(190, 60, 50, 0.8); border-radius: 4px;
  padding: 5px 14px; white-space: nowrap;
  box-shadow: inset 0 0 0 1px rgba(190, 60, 50, 0.4);
  background: rgba(16, 6, 5, 0.45);
  pointer-events: none;
}

.hz-chip-dark {
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase;
  color: var(--color-gold); background: rgba(10, 7, 16, 0.82);
  padding: 3px 9px; border: 1px solid color-mix(in srgb, var(--color-gold) 35%, transparent); border-radius: 999px;
}
.hz-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-family: var(--font-heading, serif); font-size: 13px; color: var(--color-gold);
  padding: 2px 9px;
  border: 1px solid color-mix(in srgb, var(--color-gold) 25%, transparent);
  border-radius: 999px;
  background: rgba(200, 162, 76, 0.06);
  font-variant-numeric: tabular-nums;
}
.hz-chip-xp { color: var(--color-purple-light); border-color: color-mix(in srgb, var(--color-purple-light) 30%, transparent); background: rgba(154, 134, 171, 0.07); }
.hz-chip-first { color: var(--color-gold-bright); border-color: color-mix(in srgb, var(--color-gold-bright) 35%, transparent); background: rgba(227, 200, 120, 0.07); }

@media (prefers-reduced-motion: reduce) {
  .hz-active, .hz-pip-now { animation: none; }
  .hz-card, .hz-card:hover, .hz-cover { transition: none; transform: none; }
}
</style>
